// GanttWidget — renders the `gantt-data` envelope using
// @wamra/gantt-task-react. Driven by `render_gantt` (helper-server), which
// emits one row per phase + task with start/end/dependencies parsed from
// TaskJuggler's schedule.csv.
//
// Envelope shape:
//   {
//     tasks: Array<{
//       id, wbs_id, name, start, end, duration_days,
//       dependencies: string[],          // bare task ids, prefix-stripped
//       resources: string,
//       type: "project" | "task" | "milestone",
//       is_phase: boolean,
//     }>,
//     iframe_url?: string,
//     source_path: string,
//   }
//
// Phase rows render as `project` bars (slate / amber), leaf tasks as sky
// bars, zero-duration tasks as amber milestone diamonds. Dependency arrows
// draw between every (predecessor end) → (task start).
//
// Click behaviour: clicking a row or bar pushes a property-grid envelope
// for the task into ShellContext.selected so the right pin pane shows the
// task detail. The view's "What-if" button reads the same selection to
// pre-fill its chat prompt with the right task id.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Gantt,
  ViewMode,
  TitleColumn,
  DateStartColumn,
  DateEndColumn,
  type Task,
  type Dependency,
  type Distances,
  type Column,
  type TaskOrEmpty,
} from "@wamra/gantt-task-react";
import "@wamra/gantt-task-react/dist/style.css";
import { useShell } from "../ShellContext.js";
import type { Envelope } from "../../lib/envelope.js";

interface GanttTaskRaw {
  id?: string;
  wbs_id?: string;
  name?: string;
  start?: string;
  end?: string;
  duration_days?: number;
  dependencies?: string[];
  resources?: string;
  type?: string;
  is_phase?: boolean;
}

interface GanttDataPayload {
  tasks?: GanttTaskRaw[];
  rows?: GanttTaskRaw[];
  critical_ids?: string[];
  summary?: {
    task_count?: number;
    phase_count?: number;
    dependency_count?: number;
    critical_count?: number;
  };
  iframe_url?: string | null;
}

interface Props {
  data: unknown;
  title: string;
}

// Column widths are tuned so two-to-four columns fit inside the chart panel
// at typical viewport widths (≥ 1280 px). Quarter is intentionally narrow so
// at least three quarters are visible by default.
const VIEW_MODES: Array<{ id: ViewMode; label: string; columnWidth: number }> = [
  { id: ViewMode.Day,         label: "Day",     columnWidth: 36 },
  { id: ViewMode.Week,        label: "Week",    columnWidth: 90 },
  { id: ViewMode.Month,       label: "Month",   columnWidth: 130 },
  { id: ViewMode.QuarterYear, label: "Quarter", columnWidth: 160 },
];

// Three visible task-list columns; the library's default also renders
// Dependencies / Edit / Add / Delete which we don't want — no backend wired.
const COLUMNS: readonly Column[] = [
  { id: "title",      Cell: TitleColumn,     width: 280, title: "Name" },
  { id: "date_start", Cell: DateStartColumn, width: 100, title: "From" },
  { id: "date_end",   Cell: DateEndColumn,   width: 100, title: "To" },
];

export function GanttWidget({ data }: Props) {
  const [view, setView] = useState<ViewMode>(ViewMode.Week);
  const [criticalOnly, setCriticalOnly] = useState(false);
  const { setSelected, openChat } = useShell();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  // Keep a ref on the chart pane so the Gantt SVG anchors against it.
  const chartHostRef = useRef<HTMLDivElement | null>(null);
  // @wamra/gantt-task-react uses ResizeObserver on its container to lay out
  // the chart. On the first mount the container's box hasn't settled by
  // the time the library starts observing, so the chart paints empty
  // until the next layout change. Nudging the host's max-width by 1 px
  // and back fires the ResizeObserver and triggers the library to render
  // every row + bar.
  const taskCount = (data as GanttDataPayload | undefined)?.tasks?.length ?? 0;
  useEffect(() => {
    if (taskCount === 0) return;
    const el = chartHostRef.current;
    if (!el) return;
    const nudge = () => {
      const prev = el.style.maxWidth;
      el.style.maxWidth = `${el.clientWidth - 1}px`;
      requestAnimationFrame(() => { el.style.maxWidth = prev; });
    };
    const t1 = window.setTimeout(nudge, 50);
    const t2 = window.setTimeout(nudge, 250);
    const t3 = window.setTimeout(nudge, 600);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); window.clearTimeout(t3); };
  }, [taskCount, view, criticalOnly]);

  const distances: Partial<Distances> = useMemo(() => {
    const m = VIEW_MODES.find((v) => v.id === view) ?? VIEW_MODES[1];
    return {
      // Slim the task-list panel so the chart panel has room for several
      // columns at typical viewport widths.
      tableWidth: 360,
      titleCellWidth: 240,
      dateCellWidth: 60,
      columnWidth: m.columnWidth,
      rowHeight: 30,
      headerHeight: 44,
      // barFill defaults to 60 (% of rowHeight). At 60 the bar is 18 px in
      // a 30-px row, leaving 12 px of empty band that visually reads as a
      // second row stripe. Bump to 90 so the bar fills the row cleanly.
      barFill: 90,
    };
  }, [view]);

  const { tasks, viewDate, iframeUrl, rawById, summary } = useMemo(() => {
    const payload = (data ?? {}) as GanttDataPayload;
    const raw = payload.tasks ?? payload.rows ?? [];
    const criticalSet = new Set(payload.critical_ids ?? []);

    // Index phase rows so leaf tasks can declare them as `parent` —
    // the library then renders the tree indented + collapsible.
    const phaseIds = new Set(
      raw.filter((t) => t.is_phase && t.id).map((t) => String(t.id)),
    );
    // The leaf-task id in schedule.csv is e.g. `audit_baseline_visibility`
    // (already prefix-stripped by the helper server). The phase id is the
    // WBS prefix (e.g. `audit_baseline`). To recover the parent, look at
    // the leaf's wbs_id: `<phase>.<leaf>`.
    const phaseByLeafId: Record<string, string> = {};
    for (const t of raw) {
      if (!t.is_phase && t.wbs_id && t.wbs_id.includes(".")) {
        const parent = t.wbs_id.split(".", 1)[0];
        if (phaseIds.has(parent) && t.id) {
          phaseByLeafId[String(t.id)] = parent;
        }
      }
    }

    const out: Task[] = [];
    const rawIndex: Record<string, GanttTaskRaw> = {};
    let earliest: Date | null = null;

    for (const t of raw) {
      if (!t || !t.start || !t.end) continue;
      const startDate = new Date(t.start);
      const endDate = new Date(t.end);
      if (endDate <= startDate) {
        endDate.setHours(startDate.getHours() + 12);
      }
      const id = String(t.id ?? t.wbs_id ?? out.length);
      rawIndex[id] = t;
      const isPhase = !!t.is_phase || t.type === "project";
      const isMilestone = t.type === "milestone" || (t.duration_days ?? -1) === 0;
      const isCritical = criticalSet.has(id);
      if (!earliest || startDate < earliest) earliest = startDate;
      out.push({
        id,
        name: cleanName(t.name ?? id),
        start: startDate,
        end: endDate,
        progress: 0,
        type: isPhase ? "project" : (isMilestone ? "milestone" : "task"),
        parent: !isPhase ? phaseByLeafId[id] : undefined,
        dependencies: (t.dependencies ?? []).map<Dependency>((d) => ({
          sourceId: String(d),
          sourceTarget: "endOfTask",
          ownTarget: "startOfTask",
        })),
        styles: isPhase
          ? { barBackgroundColor: "#fde68a", barProgressColor: "#f59e0b",
              barBackgroundSelectedColor: "#fcd34d", barProgressSelectedColor: "#d97706" }
          : isMilestone
            ? (isCritical
                ? { barBackgroundColor: "#fb7185", barProgressColor: "#e11d48",
                    barBackgroundSelectedColor: "#e11d48", barProgressSelectedColor: "#be123c" }
                : { barBackgroundColor: "#fbbf24", barProgressColor: "#d97706",
                    barBackgroundSelectedColor: "#f59e0b", barProgressSelectedColor: "#b45309" })
            : (isCritical
                ? { barBackgroundColor: "#f43f5e", barProgressColor: "#e11d48",
                    barBackgroundSelectedColor: "#e11d48", barProgressSelectedColor: "#9f1239" }
                : { barBackgroundColor: "#0ea5e9", barProgressColor: "#0284c7",
                    barBackgroundSelectedColor: "#0369a1", barProgressSelectedColor: "#075985" }),
        isDisabled: false,
      } as Task);
    }
    // Optional "show critical only" filter — keep phase rollups so the
    // tree structure remains visible above critical leaves.
    const filtered = criticalOnly
      ? out.filter((t) => t.type === "project" || criticalSet.has(String(t.id)))
      : out;
    return {
      tasks: filtered,
      viewDate: earliest ?? new Date(),
      iframeUrl: payload.iframe_url ?? null,
      rawById: rawIndex,
      summary: payload.summary ?? null,
    };
  }, [data, criticalOnly]);

  // Push the selected task into ShellContext.selected so the right pin
  // pane shows its property grid. Clear on unmount.
  useEffect(() => {
    if (!selectedTaskId || !rawById[selectedTaskId]) {
      setSelected(null);
      return;
    }
    const raw = rawById[selectedTaskId];
    const propertyGridEnv: Envelope = {
      title: `Task ${selectedTaskId} — ${cleanName(raw.name ?? selectedTaskId)}`,
      artifact_type: "property-grid",
      data: {
        id: selectedTaskId,
        wbs_id: raw.wbs_id,
        name: cleanName(raw.name ?? ""),
        type: raw.is_phase ? "phase" : (raw.type ?? "task"),
        start: raw.start,
        end: raw.end,
        duration_days: raw.duration_days,
        dependencies: (raw.dependencies ?? []).join(", ") || "—",
        resources: raw.resources || "—",
      },
      audit: {
        tool: "render_gantt (selection)",
        produced_at: new Date().toISOString(),
        freshness: "live",
      },
    };
    setSelected({ envelope: propertyGridEnv });
    return () => setSelected(null);
  }, [selectedTaskId, rawById, setSelected]);

  if (tasks.length === 0) {
    return (
      <div className="p-6 text-sm text-slate-500">
        No Gantt rows. The helper server emitted a <code>gantt-data</code>
        envelope but the <code>tasks</code> array was empty — usually
        because <code>schedule.csv</code> is missing. Rebuild the project
        to regenerate it.
        {iframeUrl && (
          <div className="mt-2">
            <a href={iframeUrl} target="_blank" rel="noreferrer" className="text-sky-700 underline">
              open the TaskJuggler HTML view instead →
            </a>
          </div>
        )}
      </div>
    );
  }

  const handleClick = (task: TaskOrEmpty) => {
    const id = String(task.id ?? "");
    setSelectedTaskId((curr) => (curr === id ? null : id));
  };

  const whatIfPrompt = selectedTaskId
    ? `Run a what-if: delay task ${selectedTaskId} by 5 days, then show the new project end date and critical-path diff.`
    : `Click a Gantt row first, then press What-if to pre-fill a delay simulation.`;

  return (
    <div className="flex h-full w-full flex-col bg-white">
      {/* Wireframe-style toolbar: view-mode pills + critical toggle on left,
          legend + counts on right. Sits flush against the chart with no
          intermediary metarow. */}
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5">
            {VIEW_MODES.map((m) => (
              <button
                key={m.label}
                type="button"
                onClick={() => setView(m.id)}
                className={
                  "rounded px-2 py-0.5 " +
                  (view === m.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")
                }
              >{m.label}</button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setCriticalOnly((v) => !v)}
            className={
              "rounded border px-2 py-0.5 " +
              (criticalOnly
                ? "border-rose-300 bg-rose-100 text-rose-700"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100")
            }
            title="Toggle critical-only filter"
          >Show critical only</button>
          <button
            type="button"
            disabled={!selectedTaskId}
            onClick={() => openChat(whatIfPrompt)}
            className={
              "rounded border px-2 py-0.5 " +
              (selectedTaskId
                ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                : "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400")
            }
            title={selectedTaskId ? `What-if on ${selectedTaskId}` : "Click a task first"}
          >What-if{selectedTaskId ? ` (${selectedTaskId})` : ""}</button>
          {selectedTaskId && (
            <button
              type="button"
              onClick={() => setSelectedTaskId(null)}
              className="rounded border border-slate-200 bg-white px-2 py-0.5 text-slate-600 hover:bg-slate-100"
              title="Clear selection"
            >clear selection</button>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-600">
          <LegendChip className="bg-sky-500" label="task" />
          <LegendChip className="bg-rose-500" label="critical" />
          <LegendChip className="bg-amber-300" label="phase rollup" />
          <LegendChip className="bg-amber-400" label="milestone" />
          {summary && (
            <span className="text-slate-500">
              {summary.task_count ?? "?"} tasks · {summary.phase_count ?? "?"} phases · {summary.dependency_count ?? "?"} dependencies
            </span>
          )}
          {iframeUrl && (
            <a
              href={iframeUrl}
              target="_blank"
              rel="noreferrer"
              className="text-slate-500 underline-offset-2 hover:text-sky-700 hover:underline"
            >TJ HTML ↗</a>
          )}
        </div>
      </div>
      {/* Vertical-only scroll on the outer wrapper. The library has its
          own horizontal scroll on `_ganttTaskRoot_` (overflow-x: scroll);
          if our wrapper also captured horizontal wheel/drag events the
          chart would be unresponsive to horizontal panning. `overflow-x:
          hidden` lets horizontal interactions fall through to the
          library's internal scroll while vertical interactions still
          scroll our wrapper through all rows. */}
      <div ref={chartHostRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden" style={{ width: "100%" }}>
        <Gantt
          tasks={tasks}
          viewMode={view}
          viewDate={viewDate}
          preStepsCount={0}
          distances={distances}
          columns={COLUMNS}
          canMoveTasks={false}
          canResizeColumns={true}
          onClick={handleClick}
          isShowChildOutOfParentWarnings={false}
          isShowDependencyWarnings={false}
        />
      </div>
    </div>
  );
}

// schedule.csv keeps the leading 2-space indent on task names; the
// markup ("1.1 Audit & Baseline") is fine for phases but task names
// shouldn't carry the indent. Trim — the tree indent + bar colour now
// signals the hierarchy.
function cleanName(s: string): string {
  return s.replace(/^\s+/, "").trim();
}

function LegendChip({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`h-2.5 w-2.5 rounded-sm ${className}`} />
      <span>{label}</span>
    </span>
  );
}
